import React, { useState } from 'react';
import { Content, Header, Page, ContentHeader } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CheckIcon from '@material-ui/icons/Check';

const useStyles = makeStyles(theme => ({
  root: {
    '& > * + *': {
      marginTop: theme.spacing(3),
    },
  },
  step: {
    marginTop: theme.spacing(4),
  },
  codeBlock: {
    position: 'relative',
    marginTop: theme.spacing(2),
    backgroundColor: '#263238',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  codeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    backgroundColor: '#37474F',
    borderBottom: '1px solid #455A64',
  },
  codeTitle: {
    color: theme.palette.common.white,
    fontWeight: 600,
    fontSize: '0.85rem',
  },
  copyButton: {
    color: theme.palette.common.white,
    textTransform: 'none',
    fontSize: '0.75rem',
  },
  pre: {
    margin: 0,
    padding: theme.spacing(2),
    overflow: 'auto',
    maxHeight: 500,
  },
  code: {
    fontFamily: '"Fira Code", "Consolas", monospace',
    fontSize: '0.85rem',
    lineHeight: 1.5,
    color: '#ECEFF1',
    whiteSpace: 'pre',
  },
  keyword: { color: '#82AAFF' },
  string: { color: '#C3E88D' },
  number: { color: '#F78C6C' },
  comment: { color: '#546E7A', fontStyle: 'italic' },
  key: { color: '#F07178' },
  instruction: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    marginTop: theme.spacing(2),
  },
  noteBox: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    marginTop: theme.spacing(2),
  },
  fieldList: {
    marginTop: theme.spacing(2),
    '& li': {
      marginBottom: theme.spacing(1),
    },
  },
}));

const COMPONENT_YAML = `apiVersion: backstage.io/v1alpha1
kind: Component
metadata:
  name: asmik-landing-page-website
  description: ASMIK Landing Page Website
  links:
    - url: https://landingdpl.axa-id.intrax/asmik-landing-page-acc/
      title: ASMIK Landing Page
      icon: dashboard
  tags:
    - react
    - typescript
    - vite
  annotations:
    sonarqube.org/project-key: asmik-bankmandiri-landing-page
    jenkins.io/job-full-name: asmik-bankmandiri-landing-page
    amfs.io/architecture-diagram-url: https://git.amfs-sandbox.com/AMFS/asmik-bankmandiri-landing-page...
    amfs.io/source-location: url:https://git.amfs-sandbox.com/AMFS/asmik-bankmandiri-landing-page...
spec:
  type: website
  lifecycle: development
  owner: sysdev
  system: asmik
  consumesApis:
    - api:default/asmik-landing-page-service
  dependsOn:
    - component:default/data
    - component:default/search
    - resource:default/kafka
    - resource:default/elastic-search`;

const API_YAML = `apiVersion: backstage.io/v1alpha1
kind: Component
metadata:
  name: asmik-landing-page-service
  description: ASMIK Landing Page Services
  links:
    - url: https://swagger...
      title: ASMIK Landing Page Swagger
      icon: dashboard
  tags:
    - net
    - microservice
  annotations:
    sonarqube.org/project-key: asmik-bankmandiri-landing-page-services
    jenkins.io/job-full-name: asmik-bankmandiri-landing-page-services
    amfs.io/architecture-diagram-url: https://git.amfs-sandbox.com/AMFS/asmik-bankmandiri-landing-page-services...
    amfs.io/source-location: url:https://git.amfs-sandbox.com/AMFS/asmik-bankmandiri-landing-page-services...
spec:
  type: service
  lifecycle: development
  owner: sysdev
  system: asmik
  providesApis:
    - component:default/asmik-bankmandiri-landing-page
  consumesApis:
    - api:default/edocs
  dependsOn:
    - component:default/data
    - component:default/search
    - resource:default/kafka
    - resource:default/elastic-search
---
apiVersion: backstage.io/v1alpha1
kind: API
metadata:
  name: asmik-landing-page-service
  description: ASMIK Landing Page Service API
  links:
    - url: https://landingdpl.axa-id.intrax/asmik-landing-page-service-pontswag/swagger
      title: ASMIK Landing Page Service Swagger
      icon: services
  annotations:
    amfs.io/source-location: url:https://git.amfs-sandbox.com/AMFS/amfs-portal-ba...
    amfs.io/docs-url: https://axamandiri.atlassian.net/wiki/download/attachments/...
spec:
  type: openapi
  lifecycle: development
  tags:
    - uit
    - mfe
  owner: sysdev
  system: asmik`;

const CodeBlock = ({ title, code }: { title: string; code: string }) => {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Paper elevation={2} className={classes.codeBlock}>
      <div className={classes.codeHeader}>
        <Typography className={classes.codeTitle}>{title}</Typography>
        <Button
          size="small"
          startIcon={copied ? <CheckIcon /> : <FileCopyIcon />}
          onClick={handleCopy}
          className={classes.copyButton}
        >
          {copied ? 'Copied!' : 'Copy'}
        </Button>
      </div>
      <pre className={classes.pre}>
        <code className={classes.code}>{code}</code>
      </pre>
    </Paper>
  );
};

export const GuidePage = () => {
  const classes = useStyles();

  return (
    <Page themeId="documentation">
      <Header
        title="How To Add Component or API Catalog in Backstage"
        subtitle="Step-by-step guide to register your services and APIs in the AMFS catalog"
      />
      <Content>
        <div className={classes.root}>
          <Typography variant="body1">
            Follow the steps below to onboard your component or API to the Backstage catalog.
          </Typography>

          <div className={classes.step}>
            <Typography variant="h5" gutterBottom>
              1. Create an entity YAML file
            </Typography>
            <Typography variant="body1">
              Create an entity YAML file named <strong>catalog-info.yaml</strong> in the repo.
              You can keep entities in a single file or split them by type.
            </Typography>

            <Typography variant="h6" style={{ marginTop: 16 }}>
              Component example
            </Typography>
            <CodeBlock title="catalog-info.yaml" code={COMPONENT_YAML} />

            <Typography variant="h6" style={{ marginTop: 16 }}>
              API example (For backend/service) - Note: For registering backend/service, please create 2 kind: "Component" and "API" with same content
            </Typography>
            <CodeBlock title="catalog-info.yaml" code={API_YAML} />
          </div>

          <div className={classes.step}>
            <Typography variant="h5" gutterBottom>
              2. Register the YAML in Backstage (UI)
            </Typography>
            <div className={classes.instruction}>
              <ol>
                <li>Open Backstage and go to <strong>Catalog</strong>.</li><br />
                <li>Click <strong>Register Existing Component</strong>.</li><br />
                <li>Paste the URL to your entity YAML file.<br />
                  <em>Example:</em>{' '}
                  <code>https://git.amfs-sandbox.com/AMFS/SERVICE-OR-COMPONENT-NAME/src/branch/BRANCH-NAME/catalog-info.yaml</code>
                </li><br />
                <li>Click <strong>Analyze</strong> and then <strong>Register</strong>.</li>
              </ol>
            </div>
          </div>

          <div className={classes.step}>
            <Typography variant="h5" gutterBottom>
              Field notes and common use in YAML file
            </Typography>
            <div className={classes.noteBox}>
              <ul className={classes.fieldList}>
                <li><strong>apiVersion:</strong> Catalog schema version. Most Backstage entity YAML uses <code>backstage.io/v1alpha1</code>.</li>
                <li><strong>kind:</strong> Entity type such as <code>Component</code>, <code>API</code>, <code>Resource</code>, <code>System</code>, or <code>Group</code>.</li>
                <li><strong>metadata.name:</strong> Unique, URL-safe identifier (lowercase, hyphen). <em>Example:</em> <code>my-service-app</code>.</li>
                <li><strong>metadata.description:</strong> Short summary shown in the catalog UI.</li>
                <li><strong>metadata.links:</strong> Links for website, swagger, etc.<br />
                  <em>Example:</em> <code>url: https://landingdpl.axa-id.intrax/asmik-landing-page</code> — title: ASMIK Landing Page — icon: dashboard
                </li>
                <li><strong>metadata.tags:</strong> Optional labels used for search and filters.<br />
                  <em>Example:</em> <code>service</code>, <code>node</code>, <code>payments</code>.
                </li>

<li>
  <strong>metadata.annotations:</strong> Extra metadata for tooling and integrations.<br />
  
  <em>sonarqube.org/project-key:</em> Example: <code>PROJECT_NAME_KEY</code> 
  (e.g., <code>asmik-bankmandiri-landing-page</code>).<br />
  
  <em>jenkins.io/job-full-name:</em> Example: <code>PROJECT_NAME_KEY</code> 
  (e.g., <code>asmik-bankmandiri-landing-page</code>).<br />
  
  <em>amfs.io/architecture-diagram-url:</em> Example: <code>DOCS_URL</code>.<br />
  
  <em>amfs.io/source-location:</em> Example: <code>REPO_URL</code>.<br /><br />

  <em>
    <strong>IMPORTANT:</strong> Make sure the <strong>SonarQube project key</strong> and 
    <strong> Jenkins project key</strong> exactly match the key defined in SonarQube.
    Keys are <strong>case-sensitive</strong>.<br />

    If the key does not match (even in uppercase/lowercase), SonarQube data 
    (coverage, bugs, etc.) will <strong>not be displayed in the IDP (Backstage)</strong>.<br /><br />

    <strong>Example (Incorrect):</strong><br />
    catalog-info.yaml: <code>edm-api</code><br />
    SonarQube: <code>EDM-API</code><br />
    → Result: ❌ Not shown in IDP<br /><br />

    <strong>Example (Correct):</strong><br />
    catalog-info.yaml: <code>EDM-API</code><br />
    SonarQube: <code>EDM-API</code><br />
    → Result: ✅ Data displayed correctly
  </em><br />
</li>

                <li><strong>spec.type:</strong> Domain-specific type.<br />
                  <em>Common values for Component:</em> <code>service</code>, <code>website</code>, <code>library</code>, <code>mobile</code>. <br />
                  <em>Common values for API:</em> <code>openapi</code>, <code>graphql</code>, <code>grpc</code>.
                </li>
                <li><strong>spec.lifecycle:</strong> Stage in the product lifecycle.<br />
                  <em>Common values:</em> <code>experimental</code>, <code>production</code>, <code>deprecated</code>.
                </li>
                <li><strong>spec.owner:</strong> Owning team or group, typically <code>group:team-name</code>.</li>
                <li><strong>spec.system:</strong> Logical system to group related entities. <em>Example:</em> <code>payments-platform</code>.</li>
                <li><strong>spec.dependsOn:</strong> Links a <code>Component</code> to one or more <code>API</code> entities by name.</li>
                <li><strong>spec.providesApis:</strong> Links a <code>Component</code> to one or more <code>API</code> entities by name.</li>
                <li><strong>spec.definition:</strong> API contract content (<code>OpenAPI/AsyncAPI/GraphQL</code>).<br />
                  Use a <code>$</code> block for inline specs, or a <code>url:</code> to the definition if you host it externally.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Content>
    </Page>
  );
};
